import React, { useState, useEffect } from 'react';
import { Star, MapPin, MessageSquare } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TestimonialsSection = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    rating: 5,
    comment: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/testimonials?approved_only=true`);
      setTestimonials(response.data);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${BACKEND_URL}/api/testimonials`, formData);
      toast.success('Thank you for your testimonial! It will be reviewed and published soon.');
      
      // Reset form
      setFormData({
        name: '',
        location: '',
        rating: 5,
        comment: ''
      });
      setShowForm(false);
    } catch (error) {
      console.error('Testimonial error:', error);
      toast.error('Failed to submit testimonial. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="testimonials" className="section-padding">
      <div className="container">
        <div className="text-center mb-12">
          <h2 className="heading-1 mb-4">What Our Patients Say</h2>
          <p className="body-large max-w-3xl mx-auto">
            Read about the experiences of patients who have received care from Dr Meer.
          </p>
        </div>

        {/* Testimonials Grid - only show if there are testimonials */}
        {testimonials.length > 0 && (
          <div className="testimonials-grid mb-12">
            {testimonials.map((testimonial) => (
              <div key={testimonial.id} className="testimonial-card">
                <div className="testimonial-header">
                  <div className="testimonial-stars">
                    {[...Array(testimonial.rating)].map((_, index) => (
                      <Star key={index} size={16} fill="currentColor" />
                    ))}
                  </div>
                  <div className="testimonial-date body-small">
                    {new Date(testimonial.created_at).toLocaleDateString('en-ZA', { 
                      month: 'short', 
                      year: 'numeric' 
                    })}
                  </div>
                </div>

                <p className="testimonial-comment body-medium">
                  "{testimonial.comment}"
                </p>

                <div className="testimonial-footer">
                  <div className="testimonial-author">
                    <div className="author-avatar">
                      {testimonial.name.charAt(0)}
                    </div>
                    <div>
                      <div className="heading-3">{testimonial.name}</div>
                      <div className="body-small flex items-center gap-1">
                        <MapPin size={12} />
                        {testimonial.location}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* We Value Your Feedback - ALWAYS visible */}
        <div className="text-center">
          <div className="w-16 h-16 bg-medical-red/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <MessageSquare size={32} className="text-medical-red" />
          </div>
          <h3 className="heading-3 mb-2">We Value Your Feedback</h3>
          <p className="body-medium text-text-muted mb-6">
            Have you experienced care from <span style={{ whiteSpace: 'nowrap' }}>Dr Meer</span>? Your testimonial helps other clients make informed decisions about their healthcare.
          </p>
          {!showForm && (
            <button
              onClick={() => setShowForm(true)}
              className="btn-primary"
            >
              Share Your Experience
            </button>
          )}
        </div>

        {/* Testimonial Form - only show when clicking Share button */}
        {showForm && (
          <div className="max-w-2xl mx-auto bg-card p-8 rounded-lg shadow-md mt-8">
            <h3 className="heading-2 mb-6">Share Your Experience</h3>
            
            <form onSubmit={handleSubmit} className="text-left">
              <div className="form-group">
                <label htmlFor="name" className="form-label">Your Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="form-input"
                  placeholder="Enter your name"
                />
                </div>

                <div className="form-group">
                  <label htmlFor="location" className="form-label">Location *</label>
                  <input
                    type="text"
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="e.g., Westville, Durban"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="rating" className="form-label">Rating *</label>
                  <select
                    id="rating"
                    name="rating"
                    value={formData.rating}
                    onChange={handleChange}
                    required
                    className="form-input"
                  >
                    <option value="5">⭐⭐⭐⭐⭐ Excellent</option>
                    <option value="4">⭐⭐⭐⭐ Very Good</option>
                    <option value="3">⭐⭐⭐ Good</option>
                    <option value="2">⭐⭐ Fair</option>
                    <option value="1">⭐ Poor</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="comment" className="form-label">Your Review *</label>
                  <textarea
                    id="comment"
                    name="comment"
                    value={formData.comment}
                    onChange={handleChange}
                    required
                    rows="5"
                    className="form-input"
                    placeholder="Share your experience with Dr Meer and the service you received..."
                  />
                </div>

                <div className="flex gap-4">
                  <button 
                    type="submit" 
                    className="btn-primary flex-1"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Testimonial'}
                  </button>
                  <button 
                    type="button" 
                    onClick={() => setShowForm(false)}
                    className="btn-secondary flex-1"
                  >
                    Cancel
                  </button>
                </div>

                <p className="body-small text-center text-text-muted mt-4">
                  Your testimonial will be reviewed before being published on the website.
                </p>
              </form>
            </div>
          )}
      </div>
    </section>
  );
};

export default TestimonialsSection;
